import React, { useEffect, useState } from 'react';
import { Server, Loader2, ArrowLeft, ArrowRight, ArrowUp, ArrowDown, CheckCircle, AlertCircle, ShieldCheck, FileText, MapPin, Locate, UploadCloud, Trash2 } from 'lucide-react';
import {
  Silo, SiloSurvey, SiloQuestion, SiloSession,
  listSilos, getSiloSession, listSiloSurveys, fetchSiloQuestions, submitSiloResponse, siloText,
} from '../services/siloService';

type Stage = 'silos' | 'surveys' | 'runner' | 'done';

// Discovery + live runner for BYOI silo surveys. Picking a silo mints a ticket,
// connects to the silo directly, and reads its active surveys live. Answers are
// written straight back to that silo — never to Tinaney.
const SiloDirectoryView: React.FC = () => {
  const [stage, setStage] = useState<Stage>('silos');
  const [silos, setSilos] = useState<Silo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [session, setSession] = useState<SiloSession | null>(null);
  const [activeSilo, setActiveSilo] = useState<Silo | null>(null);
  const [surveys, setSurveys] = useState<SiloSurvey[]>([]);
  const [connecting, setConnecting] = useState(false);

  const [survey, setSurvey] = useState<SiloSurvey | null>(null);
  const [questions, setQuestions] = useState<SiloQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [submitting, setSubmitting] = useState(false);
  const [uploadingQ, setUploadingQ] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try { setSilos(await listSilos()); }
      catch (e) { console.error(e); setError('Could not load silos.'); }
      finally { setLoading(false); }
    })();
  }, []);

  const openSilo = async (s: Silo) => {
    setConnecting(true); setError(null); setActiveSilo(s);
    try {
      const sess = await getSiloSession(s);
      setSession(sess);
      setSurveys(await listSiloSurveys(sess));
      setStage('surveys');
    } catch (e: any) {
      console.error(e);
      setError(e?.message || 'Could not connect to this silo.');
      setActiveSilo(null);
    } finally {
      setConnecting(false);
    }
  };

  const openSurvey = async (sv: SiloSurvey) => {
    if (!session) return;
    setError(null);
    try {
      const qs = await fetchSiloQuestions(session, sv.id);
      setSurvey(sv); setQuestions(qs); setAnswers({}); setStage('runner');
    } catch (e) {
      console.error(e); setError('Could not load this survey.');
    }
  };

  const submit = async () => {
    if (!session || !survey) return;
    setSubmitting(true); setError(null);
    try {
      const payload = questions
        .filter((q) => answers[q.id] !== undefined && answers[q.id] !== '')
        .map((q) => ({ question_id: q.id, answer_value: answers[q.id] }));
      await submitSiloResponse(session, survey.id, payload);
      setStage('done');
    } catch (e: any) {
      console.error(e); setError(e?.message || 'Could not submit your response.');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => { setStage('silos'); setSession(null); setActiveSilo(null); setSurveys([]); setSurvey(null); setQuestions([]); setAnswers({}); };

  // --- Question renderer (tolerant of the silo's question types) ----------
  const renderQuestion = (q: SiloQuestion) => {
    const type = (q.question_type || '').toLowerCase();
    const opts: { id?: string; label?: any; value?: string }[] = q.config?.options || [];
    const label = (o: any) => siloText(o?.label, o?.value || o?.id || '');
    const val = answers[q.id];
    const set = (v: any) => setAnswers((a) => ({ ...a, [q.id]: v }));

    // Multi-select vs single-select. Note MULTIPLE_CHOICE is single-select in
    // Tinaney (radio); CHECKBOX / multi_* are the multi-select types. RANKING also
    // carries options but is an ORDERED list — it must never fall into these buckets
    // (it used to render as single-choice, storing one id instead of an ordered array).
    const MULTI = new Set(['checkbox', 'multi_choice', 'multi_select', 'multiple_select', 'multiselect']);
    const isRanking = type.includes('rank');
    const isMulti = !isRanking && opts.length > 0 && MULTI.has(type);
    const isSingle = !isRanking && opts.length > 0 && !isMulti
      && !(type.includes('matrix'));

    // RANKING — reorder the options; the answer is the full ordered id array.
    if (isRanking && opts.length > 0) {
      const order: string[] = Array.isArray(val) ? val : opts.map((o, i) => o.id || o.value || String(i));
      const move = (idx: number, dir: -1 | 1) => {
        const next = [...order];
        const j = idx + dir;
        if (j < 0 || j >= next.length) return;
        [next[idx], next[j]] = [next[j], next[idx]];
        set(next);
      };
      return (
        <div className="space-y-2">
          {order.map((id, idx) => {
            const o = opts.find((o, i) => (o.id || o.value || String(i)) === id);
            if (!o) return null;
            return (
              <div key={id} className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-slate-700 bg-slate-900">
                <span className="text-sm text-slate-300"><span className="text-slate-500 font-mono mr-2">{idx + 1}.</span>{label(o)}</span>
                <span className="flex gap-1">
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-slate-700 text-slate-400 disabled:opacity-30"><ArrowUp size={14} /></button>
                  <button onClick={() => move(idx, 1)} disabled={idx === order.length - 1} className="p-1 rounded hover:bg-slate-700 text-slate-400 disabled:opacity-30"><ArrowDown size={14} /></button>
                </span>
              </div>
            );
          })}
          {!Array.isArray(val) && (
            <button onClick={() => set(order)} className="text-indigo-400 text-xs hover:underline">Confirm this order</button>
          )}
        </div>
      );
    }

    // MATRIX — one choice per row across shared columns.
    const rows: any[] = q.config?.rows || [];
    const cols: any[] = q.config?.columns || [];
    if ((type.includes('matrix') || (rows.length > 0 && cols.length > 0)) && rows.length > 0 && cols.length > 0) {
      const grid: Record<string, string> = (val && typeof val === 'object' && !Array.isArray(val)) ? val : {};
      return (
        <div className="space-y-3">
          {rows.map((r, ri) => {
            const rId = r.id || String(ri);
            return (
              <div key={rId} className="bg-slate-900 rounded-lg border border-slate-700 p-3">
                <p className="text-xs text-slate-300 font-medium mb-2">{siloText(r.label, rId)}</p>
                <div className="flex flex-wrap gap-2">
                  {cols.map((c, ci) => {
                    const cId = c.id || String(ci);
                    const on = grid[rId] === cId;
                    return (
                      <button key={cId} onClick={() => set({ ...grid, [rId]: cId })}
                        className={`px-3 py-1.5 rounded-lg border text-xs transition-colors ${on ? 'border-indigo-500 bg-indigo-900/30 text-white' : 'border-slate-700 bg-slate-800 text-slate-400 hover:border-slate-600'}`}>
                        {siloText(c.label, cId)}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // DATE — native date picker instead of free text.
    if (type.includes('date')) {
      return <input type="date" value={val ?? ''} onChange={(e) => set(e.target.value)}
        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-3 text-white focus:outline-none focus:border-indigo-500 [color-scheme:dark]" />;
    }

    // GEO_LOCATION — share coordinates with proper permission/error feedback.
    if (type.includes('geo')) {
      return (
        <div className="flex flex-col items-center gap-3 py-2">
          {val ? (
            <div className="flex items-center gap-3 text-emerald-400 text-sm bg-emerald-900/15 border border-emerald-800/40 rounded-lg px-4 py-3">
              <MapPin size={16} /> <span className="font-mono">{Number(val.lat).toFixed(5)}, {Number(val.lng).toFixed(5)}</span>
              <button onClick={() => set(undefined)} className="text-xs underline text-slate-400">Clear</button>
            </div>
          ) : (
            <button onClick={() => {
              if (!navigator.geolocation) { setError('Location is not supported by this browser.'); return; }
              navigator.geolocation.getCurrentPosition(
                (p) => set({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
                () => setError('Could not get your location — please allow location access and try again.'),
                { enableHighAccuracy: true, timeout: 10000 }
              );
            }} className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-2.5 rounded-lg text-sm font-medium">
              <Locate size={16} /> Share location
            </button>
          )}
        </div>
      );
    }

    // IMAGE_UPLOAD — presign against the silo's own sign-upload function and PUT the
    // file to the silo owner's R2; the answer stored is the public URL. Fails with a
    // clear message when the silo hasn't deployed sign-upload.
    if (type.includes('image') || type.includes('upload') || type.includes('photo')) {
      const doUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !session) return;
        setUploadingQ(q.id); setError(null);
        try {
          const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
          const { data, error: fnErr } = await session.client.functions.invoke('sign-upload', {
            body: { content_type: file.type || 'application/octet-stream', ext },
          });
          if (fnErr || !data?.upload_url) throw new Error('This silo has no media upload set up (sign-upload not deployed).');
          const put = await fetch(data.upload_url, { method: 'PUT', headers: { 'Content-Type': file.type || 'application/octet-stream' }, body: file });
          if (!put.ok) throw new Error('Upload failed — please try again.');
          set(data.public_url);
        } catch (err: any) {
          setError(err?.message || 'Upload failed.');
        } finally {
          setUploadingQ(null);
        }
      };
      return (
        <div className="flex flex-col items-center gap-3 py-2">
          {val ? (
            <div className="relative">
              <img src={val} alt="Uploaded" className="max-h-48 rounded-lg border border-slate-700" />
              <button onClick={() => set(undefined)} className="absolute top-2 right-2 bg-red-600 hover:bg-red-500 text-white p-1.5 rounded-full"><Trash2 size={13} /></button>
            </div>
          ) : (
            <label className={`flex flex-col items-center gap-2 border-2 border-dashed border-slate-600 rounded-xl px-8 py-6 ${uploadingQ === q.id ? 'opacity-60 cursor-wait' : 'cursor-pointer hover:border-indigo-500'}`}>
              {uploadingQ === q.id ? <Loader2 size={22} className="animate-spin text-indigo-400" /> : <UploadCloud size={22} className="text-slate-400" />}
              <span className="text-xs text-slate-400">{uploadingQ === q.id ? 'Uploading…' : 'Upload an image'}</span>
              <input type="file" accept="image/*" onChange={doUpload} disabled={uploadingQ === q.id} className="hidden" />
            </label>
          )}
        </div>
      );
    }

    if (isMulti) {
      const arr: string[] = Array.isArray(val) ? val : [];
      return (
        <div className="space-y-2">
          {opts.map((o, i) => {
            const key = o.id || o.value || String(i);
            const on = arr.includes(key);
            return (
              <button key={key} onClick={() => set(on ? arr.filter((x) => x !== key) : [...arr, key])}
                className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-colors ${on ? 'border-indigo-500 bg-indigo-900/20 text-white' : 'border-slate-700 bg-slate-900 text-slate-300 hover:border-slate-600'}`}>
                {label(o)}
              </button>
            );
          })}
        </div>
      );
    }
    if (isSingle) {
      return (
        <div className="space-y-2">
          {opts.map((o, i) => {
            const key = o.id || o.value || String(i);
            const on = val === key;
            return (
              <button key={key} onClick={() => set(key)}
                className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-colors ${on ? 'border-indigo-500 bg-indigo-900/20 text-white' : 'border-slate-700 bg-slate-900 text-slate-300 hover:border-slate-600'}`}>
                {label(o)}
              </button>
            );
          })}
        </div>
      );
    }
    if (type.includes('scale') || type.includes('rating') || type.includes('number')) {
      const min = q.config?.min ?? 1, max = q.config?.max ?? (type.includes('number') ? 100 : 5);
      if (type.includes('number')) {
        return <input type="number" value={val ?? ''} onChange={(e) => set(e.target.value === '' ? '' : Number(e.target.value))}
          className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-3 text-white focus:outline-none focus:border-indigo-500" />;
      }
      const range = Array.from({ length: max - min + 1 }, (_, i) => min + i);
      return (
        <div className="flex flex-wrap gap-2">
          {range.map((n) => (
            <button key={n} onClick={() => set(n)}
              className={`w-11 h-11 rounded-lg border text-sm font-semibold ${val === n ? 'border-indigo-500 bg-indigo-600 text-white' : 'border-slate-700 bg-slate-900 text-slate-300 hover:border-slate-600'}`}>
              {n}
            </button>
          ))}
        </div>
      );
    }
    // text / fallback
    return (
      <textarea value={val ?? ''} onChange={(e) => set(e.target.value)} rows={3}
        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-3 text-white text-sm focus:outline-none focus:border-indigo-500"
        placeholder="Your answer" />
    );
  };

  // --- Render -------------------------------------------------------------
  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-indigo-500" size={32} /></div>;
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-in fade-in duration-300">
      <div className="border-b border-slate-700 pb-4">
        <h1 className="text-xl font-bold text-white mb-1 flex items-center gap-3"><Server className="text-indigo-500" /> Silo Surveys</h1>
        <p className="text-slate-400 text-sm">Take surveys hosted on researchers' own servers. Your answers are stored on their silo, not on Tinaney.</p>
      </div>

      {error && <div className="bg-red-900/20 border border-red-800/50 rounded-lg px-4 py-3 text-red-300 text-sm flex items-center gap-2"><AlertCircle size={16} /> {error}</div>}

      {/* Stage: pick a silo */}
      {stage === 'silos' && (
        silos.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-slate-700 rounded-2xl bg-slate-800/30">
            <div className="w-14 h-14 rounded-2xl bg-slate-900 border border-slate-700 flex items-center justify-center mx-auto mb-4"><Server size={22} className="text-slate-500" /></div>
            <p className="text-slate-300 font-medium">No silos are registered yet</p>
            <p className="text-slate-500 text-sm mt-1">Researchers who run their own Tinaney-Silo will appear here to take surveys from.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {silos.map((s) => (
              <button key={s.id} onClick={() => openSilo(s)} disabled={connecting}
                className="text-left bg-slate-800 rounded-xl border border-slate-700 p-5 hover:border-indigo-600 transition-colors disabled:opacity-60">
                <div className="flex items-center gap-3">
                  {s.logo_url
                    ? <img src={s.logo_url} alt="" className="w-11 h-11 rounded-lg object-cover border border-slate-700" />
                    : <div className="w-11 h-11 rounded-lg bg-slate-900 border border-slate-700 flex items-center justify-center"><Server size={18} className="text-indigo-400" /></div>}
                  <div className="min-w-0">
                    <h4 className="text-white font-semibold truncate">{s.name}</h4>
                    <p className="text-slate-500 text-xs flex items-center gap-1"><ShieldCheck size={11} /> Independent silo</p>
                  </div>
                  {connecting && activeSilo?.id === s.id && <Loader2 size={16} className="animate-spin text-indigo-400 ml-auto" />}
                </div>
              </button>
            ))}
          </div>
        )
      )}

      {/* Stage: pick a survey on the silo */}
      {stage === 'surveys' && (
        <div className="max-w-3xl space-y-4">
          <button onClick={reset} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white"><ArrowLeft size={15} /> All silos</button>
          <div className="flex items-center gap-2 text-white font-semibold"><Server size={16} className="text-indigo-400" /> {activeSilo?.name}</div>
          {surveys.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-8">This silo has no active surveys right now.</p>
          ) : surveys.map((sv) => (
            <button key={sv.id} onClick={() => openSurvey(sv)}
              className="w-full text-left bg-slate-800 rounded-xl border border-slate-700 p-5 hover:border-indigo-600 transition-colors">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h4 className="text-white font-semibold flex items-center gap-2"><FileText size={15} className="text-indigo-400 shrink-0" /> {siloText(sv.title, 'Untitled survey')}</h4>
                  {siloText(sv.description) && <p className="text-slate-400 text-sm mt-1 line-clamp-2">{siloText(sv.description)}</p>}
                  {sv.reward_note && <p className="text-emerald-400 text-xs mt-2">🎁 {sv.reward_note}</p>}
                </div>
                <ArrowRight size={18} className="text-slate-500 shrink-0" />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Stage: run the survey */}
      {stage === 'runner' && survey && (
        <div className="max-w-3xl space-y-5">
          <button onClick={() => setStage('surveys')} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white"><ArrowLeft size={15} /> Back</button>
          <div>
            <h2 className="text-lg font-bold text-white">{siloText(survey.title, 'Survey')}</h2>
            {siloText(survey.description) && <p className="text-slate-400 text-sm mt-1">{siloText(survey.description)}</p>}
          </div>
          {questions.map((q, i) => (
            <div key={q.id} className="bg-slate-800 rounded-xl border border-slate-700 p-5">
              <p className="text-white font-medium mb-3"><span className="text-slate-500 mr-2">{i + 1}.</span>{siloText(q.question_text, 'Question')}</p>
              {renderQuestion(q)}
            </div>
          ))}
          <button onClick={submit} disabled={submitting}
            className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3 rounded-lg disabled:opacity-50">
            {submitting ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle size={18} />} Submit response
          </button>
          <p className="text-center text-[11px] text-slate-600">Submitted directly to {activeSilo?.name}. Tinaney never sees your answers.</p>
        </div>
      )}

      {/* Stage: done */}
      {stage === 'done' && (
        <div className="max-w-lg mx-auto bg-slate-800 rounded-2xl border border-slate-700 p-8 text-center">
          <CheckCircle size={48} className="mx-auto text-emerald-400 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Thank you!</h2>
          <p className="text-slate-400 text-sm mb-6">Your response was saved on {activeSilo?.name}.</p>
          <button onClick={reset} className="bg-slate-700 hover:bg-slate-600 text-white px-6 py-2 rounded-lg text-sm font-medium">Back to silos</button>
        </div>
      )}
    </div>
  );
};

export default SiloDirectoryView;
